import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { MatSliderDragEvent } from "@angular/material/slider";
import { Subscription } from "rxjs";
import Constants from "src/app/constants/constants";
import { ScheduleDueType, ViewType } from "src/app/models/Enums";
import { ToolFilterOption } from "src/app/models/Filters/ToolFilterOption";
import { ColumnFilter, ToolFilter } from "src/app/models/ToolFilter";
import { ToolService } from "src/app/services/tool.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";

import { FilterCategory } from "./../../models/Enums";
import { ToolFilterCategory } from "./../../models/Filters/ToolFilterCategory";

@Component({
  selector: "filter-bar",
  templateUrl: "./filter-bar.component.html",
  styleUrls: ["./filter-bar.component.scss"],
  standalone: false,
})
export class FilterBarComponent implements OnDestroy {
  modelCategory: ToolFilterCategory;
  locationCategory: ToolFilterCategory;
  contractCategory: ToolFilterCategory;
  subtypeCategory: ToolFilterCategory;
  statusCategory: ToolFilterCategory;
  activeCategory: ToolFilterCategory;
  manufacturerCategory: ToolFilterCategory;
  columnFilters: ColumnFilter[] = [];
  booleanFilters: boolean[] = [];
  filter: ToolFilter;

  private _aggregations: ToolFilterCategory[];
  private _subscriptions$: Subscription[] = [];

  @Output() filterBarClose: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  set aggregations(value) {
    this._aggregations = value;
    this.filter = !this.filter ? this.toolFilterService.get(ViewType.ToolsAll) : this.filter;
    this.getToolStatistics();
    // set the state of column filters from the filter
    this.columnFilters = this.filter.columnFilters;
  }
  get aggregations() {
    return this._aggregations;
  }

  constructor(
    public toolFilterService: ToolFilterService,
    public toolService: ToolService,
    private cd: ChangeDetectorRef,
  ) {
    this._subscriptions$.push(this.onFilterChange());
  }

  onFilterChange() {
    return this.toolFilterService.toolFilterChange.subscribe((filter) => {
      this.filter = filter;
      this.getToolStatistics();
    });
  }

  getToolStatistics() {
    this.modelCategory = this.mapCategory(FilterCategory.ModelNumber);
    this.locationCategory = this.mapCategory(FilterCategory.Location);
    this.contractCategory = this.mapCategory(FilterCategory.Contract);
    this.subtypeCategory = this.mapCategory(FilterCategory.ToolSubtype);
    this.statusCategory = this.mapCategory(FilterCategory.Status);
    this.manufacturerCategory = this.mapCategory(FilterCategory.Manufacturer);
    this.activeCategory = this.mapCategory(FilterCategory.IsActive);

    // change detection here is called in order to correctly detect
    // the changes and input setters in children components
    this.cd.detectChanges();
  }

  mapCategory(categoryId: FilterCategory) {
    if (!this.aggregations) return null;

    const category = this.aggregations.find((x) => x.id == categoryId);

    if (this.filter.columnFilters)
      category.options.forEach(
        (option) => (option.selected = this.isFilterOptionSelected(option, category)),
      );

    if (categoryId === FilterCategory.Status) {
      category.options.forEach((a) => {
        const stat = this.toolService.statuses.find((b) => b.value === a.value);
        // TODO: temp solution in case we have for some reason not mapped eventStatus
        a.title = stat ? stat.title : Constants.UNKNOWN_EVENT_STATUS;
      });
    }

    return category;
  }

  private isFilterOptionSelected(option: ToolFilterOption, category: ToolFilterCategory): boolean {
    if (category.isBoolean) {
      return this.filter.booleanFilters
        ?.map((a) => a.toString())
        .includes(option.key.toLowerCase());
    }

    return this.filter.columnFilters
      .find((columnFilter) => columnFilter.property === category.id)
      ?.values?.includes(option.key);
  }

  onFilterBarChange(categoryFilter: ColumnFilter) {
    this.columnFilters = this.columnFilters.filter((x) => x.property !== categoryFilter.property);
    if (categoryFilter.values?.length > 0) {
      this.columnFilters.push(categoryFilter);
    }
    this.filter.columnFilters = this.columnFilters;
    this.toolFilterService.tempSet(this.filter);
  }

  onIsActiveFilterChange(categoryFilter: ColumnFilter) {
    this.booleanFilters = categoryFilter.values.map((a) => a.toLowerCase() === "true");
    this.filter.booleanFilters = this.booleanFilters;
    this.toolFilterService.tempSet(this.filter);
  }

  onCalibrationDueChange(event: MatSliderDragEvent) {
    // TODO: investigate why ngModel does not work sometimes
    // Set filter value in case ngModel does not work
    this.filter.calibrationDueMonths = event.value;
    this.toolFilterService.tempSet(this.filter);
  }

  onServiceDueChange(event: MatSliderDragEvent) {
    // TODO: investigate why ngModel does not work sometimes
    // Set filter value in case ngModel does not work
    this.filter.serviceDueMonths = event.value;
    this.toolFilterService.tempSet(this.filter);
  }

  get serviceDueFilterSet() {
    return this.filter.serviceDueFilters.some((x) => x == ScheduleDueType.ServiceDue);
  }
  get calibrationDueFilterSet() {
    return this.filter.serviceDueFilters.some((x) => x == ScheduleDueType.CalibrationDue);
  }

  onClearAll() {
    this.filter = this.toolFilterService.create({ asc: this.filter.asc, sort: this.filter.sort });
    this.toolFilterService.tempSet(this.filter);
  }

  onApplyFilter() {
    this.toolFilterService.set(this.filter);
    this.filterBarClose.emit();
  }

  ngOnDestroy(): void {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
